/*
 * LienzoMoments — servicios sueltos de restaurar y colorizar (DT-13)
 * ---------------------------------------------------------------------------
 * Pagina /pages/restaurar-y-colorizar. Ver la cabecera de
 * `sections/lienzomoments-photo-services.liquid` para el flujo completo.
 *
 * Aqui se decide:
 *   - si la foto sirve (mismo filtro de tipo y peso que la ficha)
 *   - que cuadro ofrece el boton B, con `LMFit.crop` y el umbral de la
 *     seccion: el formato que menos recorta, si entra en el umbral. Empate
 *     exacto, el primero de FORMAT_ORDER (3:2, el de mas margen)
 *   - que variante manda cada boton
 *   - el texto de regalo y los meses sin intereses del boton B. El plazo y
 *     el mensual llegan resueltos desde Liquid, con la aritmetica de la ficha
 *
 * Lo que viaja al pedido:
 *   properties[Foto]                 el archivo
 *   properties[Restaurar foto]       "Sí", si lo marco
 *   properties[Colorizar foto]       "Sí", si lo marco
 *   properties[Orientación foto]     vertical, horizontal o cuadrada
 * Y solo con el boton B:
 *   properties[Formato]              3:2, 1:1 o 4:3
 *   properties[Ajuste de encuadre]   solo si hay recorte que valga la pena
 *
 * La foto no se procesa: se mide y se muestra la miniatura del original.
 */
(() => {
  'use strict';

  const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_EDGE = 4000;
  const SQUARE_MARGIN = 0.03;
  const FORMAT_ORDER = ['3:2', '1:1', '4:3'];

  /* Por debajo de este recorte no se avisa de ajuste: es ruido de pixeles. */
  const NOTICEABLE_CROP = 0.01;

  const money = (cents) => '$' + Math.round(Number(cents) / 100).toLocaleString('es-MX');

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  const readJson = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch {
      return {};
    }
  };

  const measure = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ img, url, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('unreadable'));
      };
      img.src = url;
    });

  /* Igual que en la ficha: canvas, sin dependencias. */
  const compress = async (file, maxBytes) => {
    const { img, url } = await measure(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);

    for (const quality of [0.92, 0.85, 0.75, 0.65]) {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= maxBytes) {
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      }
    }
    throw new Error('tooBig');
  };

  const orientationOf = (ratio) => {
    if (Math.abs(ratio - 1) <= SQUARE_MARGIN) return 'cuadrada';
    return ratio < 1 ? 'vertical' : 'horizontal';
  };

  /** "3:2=Clásico|1:1=Cuadrado" -> { '3:2': 'Clásico', ... } */
  const parseNames = (text) =>
    String(text || '')
      .split('|')
      .map((pair) => pair.split('='))
      .reduce((names, [key, value]) => {
        if (key && value) names[key.trim()] = value.trim();
        return names;
      }, {});

  /** Protecciones disuasivas de las imagenes. Ver la cabecera de la seccion. */
  const protect = (root) => {
    root.querySelectorAll('[data-lm-protect]').forEach((figure) => {
      figure.addEventListener('contextmenu', (event) => event.preventDefault());
      figure.addEventListener('dragstart', (event) => event.preventDefault());
      figure.querySelectorAll('img').forEach((img) => img.setAttribute('draggable', 'false'));
    });
  };

  const init = (root) => {
    if (root.dataset.lmReady === 'true') return;
    root.dataset.lmReady = 'true';

    protect(root);

    const form = root.querySelector('[data-lm-svc-form]');
    if (!form || !window.LMFit) return;

    const data = readJson(root, '[data-lm-svc-data]');
    const messages = readJson(root, '[data-lm-svc-messages]');
    const services = data.services || {};
    const combos = data.combos || {};
    const formatNames = parseNames(messages.formatNames);
    const tolerance = (Number(root.dataset.lmTolerance) || 15) / 100;
    const maxBytes = (Number(root.dataset.lmMaxMb) || 18) * 1024 * 1024;

    const q = (selector) => root.querySelector(selector);
    const fileInput = q('[data-lm-svc-file]');
    const thumb = q('[data-lm-svc-thumb]');
    const dropText = q('[data-lm-svc-drop-text]');
    const drop = q('[data-lm-svc-drop]');
    const error = q('[data-lm-svc-error]');
    const servicesBox = q('[data-lm-svc-services]');
    const checks = Array.from(root.querySelectorAll('[data-lm-svc-service]'));
    const actions = q('[data-lm-svc-actions]');
    const solo = q('[data-lm-svc-solo]');
    const soloLabel = q('[data-lm-svc-solo-label]');
    const combo = q('[data-lm-svc-combo]');
    const adjustNote = q('[data-lm-svc-adjust-note]');
    const gift = q('[data-lm-svc-gift]');
    const msi = q('[data-lm-svc-combo-msi]');
    const orientationProp = q('[data-lm-svc-orientation]');
    const formatProp = q('[data-lm-svc-format]');
    const adjustProp = q('[data-lm-svc-adjust]');
    const initialDropText = dropText.textContent;

    /* La foto aceptada: su proporcion y el cuadro que le toca, o null. */
    let photo = null;

    const showError = (text) => {
      error.textContent = text;
      error.hidden = false;
    };

    const clearError = () => {
      error.textContent = '';
      error.hidden = true;
    };

    /**
     * El formato que menos recorta, si recorta `tolerance` o menos. Solo
     * cuenta un formato si hay variante del cuadro para el. Desigualdad
     * estricta: en empate exacto se queda el primero de FORMAT_ORDER.
     */
    const bestFormat = (ratio) => {
      let best = null;
      FORMAT_ORDER.forEach((format) => {
        if (!combos[format]) return;
        const loss = window.LMFit.crop(ratio, format);
        if (loss <= tolerance && (!best || loss < best.loss)) best = { format, loss };
      });
      return best;
    };

    const chosenServices = () => checks.filter((check) => check.checked).map((check) => check.dataset.lmSvcService);

    const serviceKey = (chosen) => {
      if (chosen.length === 2) return 'Restaurar y colorizar';
      return chosen[0] === 'restore' ? 'Restaurar' : 'Colorizar';
    };

    const serviceName = (chosen) => {
      if (chosen.length === 2) return messages.serviceBoth;
      return chosen[0] === 'restore' ? messages.serviceRestore : messages.serviceColorize;
    };

    const giftName = (chosen) => {
      if (chosen.length === 2) return messages.giftBoth;
      return chosen[0] === 'restore' ? messages.giftRestore : messages.giftColorize;
    };

    const render = () => {
      servicesBox.disabled = !photo;
      const chosen = photo ? chosenServices() : [];
      const variant = chosen.length ? services[serviceKey(chosen)] : null;

      actions.hidden = !variant;
      if (!variant) return;

      solo.value = variant.id;
      soloLabel.textContent = fill(messages.solo, {
        service: serviceName(chosen).toLowerCase(),
        price: money(variant.price),
      });

      const fit = photo.fit;
      const offer = fit ? combos[fit.format] : null;
      combo.hidden = !offer;
      gift.hidden = !offer;
      adjustNote.hidden = !offer || fit.loss < NOTICEABLE_CROP;
      if (!offer) return;

      const values = {
        service: serviceName(chosen),
        gift: giftName(chosen),
        size: offer.size,
        format: formatNames[fit.format] || fit.format,
        off: offer.off,
      };
      combo.value = offer.id;
      gift.textContent = fill(messages.gift, values);
      q('[data-lm-svc-combo-badge]').textContent = fill(messages.comboBadge, values);
      q('[data-lm-svc-combo-title]').textContent = fill(messages.comboTitle, values);
      q('[data-lm-svc-combo-was]').textContent = money(offer.compare);
      q('[data-lm-svc-combo-now]').textContent = money(offer.price);

      msi.hidden = !offer.msiMonths;
      if (offer.msiMonths) {
        q('[data-lm-svc-combo-monthly]').textContent = money(offer.msiMonthly);
        q('[data-lm-svc-combo-term]').textContent = fill(messages.msiTerm, { months: offer.msiMonths });
      }
    };

    const resetPhoto = () => {
      photo = null;
      fileInput.value = '';
      thumb.hidden = true;
      thumb.removeAttribute('src');
      dropText.textContent = initialDropText;
      drop.classList.remove('is-filled');
      orientationProp.value = '';
      orientationProp.disabled = true;
    };

    const accept = (file, ratio, url) => {
      if (thumb.src) URL.revokeObjectURL(thumb.src);
      thumb.src = url;
      thumb.hidden = false;
      dropText.textContent = messages.uploadChange;
      drop.classList.add('is-filled');
      orientationProp.value = orientationOf(ratio);
      orientationProp.disabled = false;
      photo = { ratio, fit: bestFormat(ratio) };
    };

    fileInput.addEventListener('change', async () => {
      const file = fileInput.files && fileInput.files[0];
      clearError();
      if (!file) {
        resetPhoto();
        return render();
      }

      try {
        if (/\.hei[cf]$/i.test(file.name)) throw new Error('heic');
        if (!ACCEPTED.includes(file.type)) throw new Error('format');

        const measured = await measure(file).catch(() => {
          throw new Error('unreadable');
        });

        if (file.size > maxBytes) {
          let smaller;
          try {
            smaller = await compress(file, maxBytes);
          } catch {
            URL.revokeObjectURL(measured.url);
            throw new Error('tooBig');
          }
          const transfer = new DataTransfer();
          transfer.items.add(smaller);
          fileInput.files = transfer.files;
        }

        accept(file, measured.width / measured.height, measured.url);
      } catch (err) {
        resetPhoto();
        showError(messages[err.message] || messages.unreadable);
      }
      render();
    });

    checks.forEach((check) => check.addEventListener('change', render));

    /*
     * El boton que envia decide que propiedades viajan. Formato y ajuste
     * solo tienen sentido con cuadro; con el servicio suelto se apagan para
     * que no lleguen al pedido.
     */
    form.addEventListener('submit', (event) => {
      const submitter = event.submitter;
      if (!photo || !submitter || !submitter.value) {
        event.preventDefault();
        return;
      }

      const withFrame = submitter === combo && photo.fit;
      formatProp.disabled = !withFrame;
      adjustProp.disabled = !withFrame || photo.fit.loss < NOTICEABLE_CROP;
      if (withFrame) {
        formatProp.value = photo.fit.format;
        adjustProp.value = `recorte de ${Math.round(photo.fit.loss * 100)} % para el formato ${photo.fit.format}`;
      }

      /* Un segundo toque mientras sube la foto duplicaria la linea. */
      root.querySelectorAll('button[type="submit"]').forEach((button) => {
        button.setAttribute('aria-disabled', 'true');
      });
      if (root.dataset.lmSubmitting === 'true') event.preventDefault();
      root.dataset.lmSubmitting = 'true';
    });

    /* Volver con el boton atras desde el checkout restaura la pagina de cache. */
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      root.dataset.lmSubmitting = 'false';
      root.querySelectorAll('button[type="submit"]').forEach((button) => button.removeAttribute('aria-disabled'));
    });

    render();
  };

  const boot = () => document.querySelectorAll('[data-lm-svc]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
